import { putDetails } from "@/common/character/details";

export interface DetailsStore {
  id: number;
  age: number;
  background: string;
  backstory: string;
  eyeColor: string;
  gender: string;
  hairColor: string;
  journal: string;
  knownLanguages: Array<KnownLanguageStore>;
  motivation: string;
  name: string;
  notes: string;
  weight: string;
  designProfile: Array<DesignProfileStore>;
  editMode: boolean;
  image: string;
}

export interface KnownLanguageStore {
  name: string;
  rank: number;
}
export interface DesignProfileStore {
  id: number;
  pictorial: boolean;
  name: string;
  defaultPageBackground: string;
  defaultTextLight: string;
  defaultTextDark: string;
  backgroundAndBorder: string;
  lightBackground: string;
  alertDefault: string;
  font: string;
}

export default {
  namespaced: true,
  state: (): DetailsStore => ({
    id: 0,
    age: 0,
    background: "",
    backstory: "",
    eyeColor: "",
    gender: "",
    hairColor: "",
    image: "",
    journal: "",
    knownLanguages: [
      { name: "", rank: 0 },
      { name: "", rank: 0 },
      { name: "", rank: 0 },
      { name: "", rank: 0 },
      { name: "", rank: 0 },
    ],
    motivation: "",
    name: "Crescent Character",
    notes: "",
    weight: "0 lbs",
    designProfile: [
      {
        id: 0,
        pictorial: true,
        name: "Default Profile",
        defaultPageBackground: "#dfdfdf",
        defaultTextLight: "#dfdfdf",
        defaultTextDark: "#000000",
        backgroundAndBorder: "#422c58",
        lightBackground: "#f1eef1",
        alertDefault: "#c15be6",
        font: "Bahnschrift",
      },
      {
        id: 0,
        pictorial: false,
        name: "Dark Profile",
        defaultPageBackground: "#34353C",
        defaultTextLight: "#757287",
        defaultTextDark: "#9595B7",
        backgroundAndBorder: "#414158",
        lightBackground: "#74747B",
        alertDefault: "#F09670",
        font: "",
      },
      {
        id: 0,
        pictorial: false,
        name: "Bonus Default Profile",
        defaultPageBackground: "#dfdfdf",
        defaultTextLight: "#dfdfdf",
        defaultTextDark: "#000000",
        backgroundAndBorder: "#422c58",
        lightBackground: "#f1eef1",
        alertDefault: "#c15be6",
        font: "",
      },
    ],
    editMode: false, // Not in the database
  }),
  getters: {
    age: (state: DetailsStore) => {
      return state.age;
    },
    background: (state: DetailsStore) => {
      return state.background;
    },
    backstory: (state: DetailsStore) => {
      return state.backstory;
    },
    image: (state: DetailsStore) => {
      return state.image;
    },
    eyeColor: (state: DetailsStore) => {
      return state.eyeColor;
    },
    id: (state: DetailsStore) => {
      return state.id;
    },
    gender: (state: DetailsStore) => {
      return state.gender;
    },
    hairColor: (state: DetailsStore) => {
      return state.hairColor;
    },
    journal: (state: DetailsStore) => {
      return state.journal;
    },
    knownLanguages: (state: DetailsStore) => {
      return state.knownLanguages;
    },
    motivation: (state: DetailsStore) => {
      return state.motivation;
    },
    name: (state: DetailsStore) => {
      return state.name;
    },
    notes: (state: DetailsStore) => {
      return state.notes;
    },
    weight: (state: DetailsStore) => {
      return state.weight;
    },
    designProfile: (state: DetailsStore) => {
      return state.designProfile;
    },
    getDesignId: (state: DetailsStore) => {
      return state.designProfile[0].id;
    },
    defaultPageBackground: (state: DetailsStore) => {
      return state.designProfile[0].defaultPageBackground;
    },
    defaultTextDark: (state: DetailsStore) => {
      return state.designProfile[0].defaultTextDark;
    },
    backgroundAndBorder: (state: DetailsStore) => {
      return state.designProfile[0].backgroundAndBorder;
    },
    defaultTextLight: (state: DetailsStore) => {
      return state.designProfile[0].defaultTextLight;
    },
    lightBackground: (state: DetailsStore) => {
      return state.designProfile[0].lightBackground;
    },
    alertDefault: (state: DetailsStore) => {
      return state.designProfile[0].alertDefault;
    },
    font: (state: DetailsStore) => {
      return state.designProfile[0].font;
    },
    details: (state: DetailsStore) => {
      return {
        id: state.id,
        age: state.age,
        background: state.background,
        backstory: state.backstory,
        image: state.image,
        eyeColor: state.eyeColor,
        gender: state.gender,
        hairColor: state.hairColor,
        journal: state.journal,
        knownLanguages: state.knownLanguages,
        motivation: state.motivation,
        name: state.name,
        notes: state.notes,
        weight: state.weight,
        designProfile: state.designProfile,
        editMode: state.editMode,
      };
    },
    getPictorial: (state: DetailsStore) => {
      return state.designProfile[0].pictorial;
    },
    editMode: (state: DetailsStore) => {
      return state.editMode;
    },
    getProfileName: (state: DetailsStore) => {
      return { ...state.designProfile[0] }.name;
    },
    getProfileNames: (state: DetailsStore) => {
      return [
        { ...state.designProfile[0] }.name,
        { ...state.designProfile[1] }.name,
        { ...state.designProfile[2] }.name,
      ];
    },
  },
  mutations: {
    setDetails: (
      state: DetailsStore,
      newData: {
        id: number;
        characterId: string;
        age: number;
        background: string;
        backstory: string;
        image: string;
        eyeColor: string;
        gender: string;
        hairColor: string;
        journal: string;
        motivation: string;
        name: string;
        notes: string;
        weight: string;
      }
    ) => {
      state.id = newData.id;
      state.age = newData.age;
      state.background = newData.background;
      state.backstory = newData.backstory;
      state.eyeColor = newData.eyeColor;
      state.gender = newData.gender;
      state.hairColor = newData.hairColor;
      state.journal = newData.journal;
      state.motivation = newData.motivation;
      state.name = newData.name;
      state.notes = newData.notes;
      state.weight = newData.weight;
      state.image = newData.image;
    },
    setAge: (state: DetailsStore, newData: number) => {
      state.age = newData;
    },
    setBackground: (state: DetailsStore, newData: string) => {
      state.background = newData;
    },
    setBackstory: (state: DetailsStore, newData: string) => {
      state.backstory = newData;
    },
    setImage: (state: DetailsStore, newData: any) => {
      state.image = newData;
    },
    setEyeColor: (state: DetailsStore, newData: string) => {
      state.eyeColor = newData;
    },
    setId: (state: DetailsStore, newData: number) => {
      state.id = newData;
    },
    setGender: (state: DetailsStore, newData: string) => {
      state.gender = newData;
    },
    setHairColor: (state: DetailsStore, newData: string) => {
      state.hairColor = newData;
    },
    setJournal: (state: DetailsStore, newData: string) => {
      state.journal = newData;
    },
    setKnownLanguages: (
      state: DetailsStore,
      newData: Array<KnownLanguageStore>
    ) => {
      state.knownLanguages = newData;
    },
    setMotivation: (state: DetailsStore, newData: string) => {
      state.motivation = newData;
    },
    setName: (state: DetailsStore, newData: string) => {
      state.name = newData;
    },
    setNotes: (state: DetailsStore, newData: string) => {
      state.notes = newData;
    },
    setWeight: (state: DetailsStore, newData: string) => {
      state.weight = newData;
    },
    setDesignName: (state: DetailsStore, name: string) => {
      state.designProfile[0].name = name;
    },
    setDesignProfile: (state: DetailsStore, newData: DesignProfileStore) => {
      state.designProfile[0].id = newData.id;
      state.designProfile[0].defaultPageBackground =
        newData.defaultPageBackground;
      state.designProfile[0].defaultTextLight = newData.defaultTextLight;
      state.designProfile[0].defaultTextDark = newData.defaultTextDark;
      state.designProfile[0].backgroundAndBorder = newData.backgroundAndBorder;
      state.designProfile[0].lightBackground = newData.lightBackground;
      state.designProfile[0].alertDefault = newData.alertDefault;
    },
    setDesignProfileAt: (
      state: DetailsStore,
      newData: DesignProfileStore & { loc: any }
    ) => {
      state.designProfile[newData.loc].defaultPageBackground =
        newData.defaultPageBackground;
      state.designProfile[newData.loc].defaultTextLight =
        newData.defaultTextLight;
      state.designProfile[newData.loc].defaultTextDark =
        newData.defaultTextDark;
      state.designProfile[newData.loc].backgroundAndBorder =
        newData.backgroundAndBorder;
      state.designProfile[newData.loc].lightBackground =
        newData.lightBackground;
      state.designProfile[newData.loc].alertDefault = newData.alertDefault;
      state.designProfile[newData.loc].name = newData.name;
      state.designProfile[newData.loc].font = newData.font;
      state.designProfile[newData.loc].pictorial = newData.pictorial;
    },
    rearrangeDesignProfiles: (state: DetailsStore, name: string) => {
      let designCopy = { ...state.designProfile };
      designCopy = [designCopy[0], designCopy[1], designCopy[2]];
      if (state.designProfile[1].name === name) {
        const old = designCopy.shift();
        if (old) {
          designCopy.push(old);
        }
      } else if (state.designProfile[2].name === name) {
        const newV = designCopy.pop();
        if (newV) {
          designCopy.unshift(newV);
        }
      }
      state.designProfile = designCopy;
    },
    toggleEditMode: (state: DetailsStore) => {
      state.editMode = !state.editMode;
    },
    togglePictorial: (state: DetailsStore) => {
      state.designProfile[0].pictorial = !state.designProfile[0].pictorial;
    },
  },
  actions: {
    putAge: (
      context: any,
      newData: {
        age: number;
        character_id: string;
      }
    ) => {
      context.commit("setAge", newData.age);
      context.dispatch("updateDetails", newData.character_id);
    },
    putBackground: (
      context: any,
      newData: {
        background: string;
        character_id: string;
      }
    ) => {
      context.commit("setBackground", newData.background);
      context.dispatch("updateDetails", newData.character_id);
    },
    putBackstory: (
      context: any,
      newData: {
        backstory: string;
        character_id: string;
      }
    ) => {
      context.commit("setBackstory", newData.backstory);
      context.dispatch("updateDetails", newData.character_id);
    },
    putImage: (
      context: any,
      newData: {
        image: string;
        character_id: string;
      }
    ) => {
      context.commit("setImage", newData.image);
      context.dispatch("updateDetails", newData.character_id);
    },
    putEyeColor: (
      context: any,
      newData: {
        eyeColor: string;
        character_id: string;
      }
    ) => {
      context.commit("setEyeColor", newData.eyeColor);
      context.dispatch("updateDetails", newData.character_id);
    },
    putGender: (
      context: any,
      newData: {
        gender: string;
        character_id: string;
      }
    ) => {
      context.commit("setGender", newData.gender);
      context.dispatch("updateDetails", newData.character_id);
    },
    putHairColor: (
      context: any,
      newData: {
        hairColor: string;
        character_id: string;
      }
    ) => {
      context.commit("setHairColor", newData.hairColor);
      context.dispatch("updateDetails", newData.character_id);
    },
    putJournal: (
      context: any,
      newData: {
        journal: string;
        character_id: string;
      }
    ) => {
      context.commit("setJournal", newData.journal);
      context.dispatch("updateDetails", newData.character_id);
    },
    putMotivation: (
      context: any,
      newData: {
        motivation: string;
        character_id: string;
      }
    ) => {
      context.commit("setMotivation", newData.motivation);
      context.dispatch("updateDetails", newData.character_id);
    },
    putName: (
      context: any,
      newData: {
        name: string;
        character_id: string;
      }
    ) => {
      context.commit("setName", newData.name);
      context.dispatch("updateDetails", newData.character_id);
    },
    putNotes: (
      context: any,
      newData: {
        notes: string;
        character_id: string;
      }
    ) => {
      context.commit("setNotes", newData.notes);
      context.dispatch("updateDetails", newData.character_id);
    },
    putWeight: (
      context: any,
      newData: {
        weight: string;
        character_id: string;
      }
    ) => {
      context.commit("setWeight", newData.weight);
      context.dispatch("updateDetails", newData.character_id);
    },
    updateDetails: (context: any, character_id: string) => {
      const details = {
        characterId: character_id,
        age: context.getters["age"],
        background: context.getters["background"],
        backstory: context.getters["backstory"],
        image: context.getters["image"],
        eyeColor: context.getters["eyeColor"],
        id: context.getters["id"],
        gender: context.getters["gender"],
        hairColor: context.getters["hairColor"],
        journal: context.getters["journal"],
        motivation: context.getters["motivation"],
        name: context.getters["name"],
        notes: context.getters["notes"],
        weight: context.getters["weight"],
      };
      putDetails(details).then((response) => {
        context.commit("setId", response.data[0].id);
      });
    },
  },
};
